import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, Sparkles } from "lucide-react";
import JobListings from "./JobListings";

const confColor = (c) => {
  const v = (c || "").toLowerCase();
  if (v === "high") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (v === "medium") return "bg-amber-50 text-amber-600 border-amber-200";
  return "bg-rose-50 text-rose-600 border-rose-200";
};

export const VERDICT = {
  in_range: { label: "In your range", pill: "border-emerald-200 bg-emerald-50 text-emerald-700", box: "border-emerald-200 bg-emerald-50/60" },
  stretch: { label: "Pays more", pill: "border-sky-200 bg-sky-50 text-sky-700", box: "border-sky-200 bg-sky-50/60" },
  below: { label: "Below your expectation", pill: "border-slate-200 bg-slate-100 text-slate-600", box: "border-slate-200 bg-slate-50" },
  unknown: { label: "Range not published", pill: "border-slate-200 bg-slate-100 text-slate-600", box: "border-slate-200 bg-slate-50" },
};

export const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—");

const RoleCard = ({ role, index, band, currency, jobs, jobsLoading, jobsError, onCreate, onCreateJob, generatingRoleId, salaryWindow, closest }) => (
  <div className="flex flex-col h-full rounded-xl bg-white border border-slate-200 p-4 hover:border-sky-200 transition-colors" data-testid={`role-card-${index}`}>
    <div className="flex items-start justify-between gap-3">
      <h4 className="font-bold text-slate-900 leading-tight">{role.title}</h4>
      {typeof role.match_pct === "number" && (
        <div className="relative h-14 w-14 shrink-0" data-testid={`role-match-ring-${index}`}>
          <svg viewBox="0 0 36 36" className="h-14 w-14 -rotate-90">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEF2F7" strokeWidth="4" />
            <circle cx="18" cy="18" r="15.5" fill="none" strokeLinecap="round" strokeWidth="4"
              stroke={role.match_pct >= 70 ? "#059669" : role.match_pct >= 45 ? "#D97706" : "#E11D48"}
              strokeDasharray={`${(role.match_pct / 100) * 97.4} 97.4`} style={{ transition: "stroke-dasharray 0.8s ease" }} />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center num-display text-xs">{role.match_pct}%</span>
        </div>
      )}
    </div>

    <div className="mt-2 flex flex-wrap gap-1.5 items-center">
      {role.matched_skills?.slice(0, 3).map((s, j) => (
        <span key={j} className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-[11px] text-slate-700">{s}</span>
      ))}
      <Badge className={`text-[10px] ${confColor(role.confidence)}`}>{role.confidence} Confidence</Badge>
    </div>

    {role.gaps?.length > 0 && (
      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid={`role-gap-box-${index}`}>
        <p className="text-[10px] font-mono uppercase tracking-wider text-amber-700">Gaps to close</p>
        <ul className="mt-1 space-y-1 text-xs text-slate-700">
          {role.gaps.slice(0, 2).map((g, j) => <li key={j} className="flex gap-1.5"><span className="mt-[6px] h-1 w-1 shrink-0 rounded-full bg-amber-400" /><span>{g}</span></li>)}
        </ul>
      </div>
    )}

    {band?.min ? (
      <div className={`mt-2 rounded-lg border p-2.5 ${VERDICT[band.verdict]?.box || VERDICT.unknown.box}`} data-testid={`role-salary-box-${index}`}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Estimated range</p>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${VERDICT[band.verdict]?.pill || VERDICT.unknown.pill}`} data-testid={`role-salary-verdict-${index}`}>
            {closest ? "Closest to your range" : VERDICT[band.verdict]?.label || VERDICT.unknown.label}
          </span>
        </div>
        <p className="mt-1 num-display text-sm text-slate-900">{currency} {fmt(band.min)}–{fmt(band.max)}</p>
        {band.basis && <p className="mt-1 text-[11px] text-slate-500">{band.basis}</p>}
      </div>
    ) : null}

    <Button onClick={() => onCreate(role)} disabled={!!generatingRoleId}
      className="mt-3 w-full bg-sky-700 hover:bg-sky-600 text-white font-semibold" data-testid={`create-role-resume-btn-${index}`}>
      {generatingRoleId === role.role_id
        ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Generating…</span>
        : <span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Create Resume</span>}
    </Button>

    <JobListings jobs={jobs} roleId={role.role_id} loading={jobsLoading} error={jobsError} onCreate={onCreateJob}
      generatingId={generatingRoleId} salaryWindow={salaryWindow} currency={currency} />
  </div>
);

export default RoleCard;
